import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Shell from "../components/Shell";
import SchrittKunde from "../components/wizard/Kunde";
import SchrittGarten from "../components/wizard/Garten";
import SchrittLeistungen from "../components/wizard/Leistungen";
import { SchrittPreis, SchrittSenden } from "../components/wizard/PreisUndSenden";
import {
  useKatalog, useKunde, useKundenListe,
  useCreateVertrag, usePatchVertrag, useVertragVerwerfen, useVertrag, useVertraege,
  useKandidaten, useVertragPruefung, useZustimmungErstellen,
} from "../hooks/useVertraege";
import type { VertragCreateBody } from "../hooks/useVertraege";
import { useAutoSave, SPEICHER_TEXT } from "../hooks/useEntwurf";
import { useGartenprofil } from "../hooks/useGartenprofil";
import { nebenkostenNachziehen } from "../lib/frequenz";
import {
  kalkVorschau, positionAusKatalog, positionAusGespeichert, positionenAusLegacy, grundausstattung,
} from "../lib/wizard";
import type { VertragPosition } from "../lib/wizard";
import { fehlertext } from "../lib/fehler";
import "../styles/wizard.css";

// „Konditionen" ist am 28.09.2026 entfallen (Benjamin: „die Seite kann weg").
// Zahlweise und Zahlungsweg wählt der Kunde auf seiner Angebotsseite; die
// Laufzeit folgt aus dem Kalenderjahr, die Kündigungsfrist aus der
// Vertragsart. Was blieb — Vertragsbeginn und Sondervereinbarungen — steht
// jetzt im letzten Schritt. Der heißt seit Etappe C (09.10.2026) „Prüfen und
// senden" und endet mit dem Angebot an den Kunden statt mit einem zweiten
// Abschluss auf der Vertragsseite.
const STEPS = ["Kunde", "Garten", "Leistungen", "Preis", "Prüfen und senden"];

/**
 * Vertrags-Wizard. Fünf Schritte, am Ende geht das Angebot an den Kunden.
 *
 * Der Vertrag ist ab dem gewählten Kunden ein **echter Entwurf in der
 * Datenbank**, der im Hintergrund fortgeschrieben wird — wie ein Angebot in
 * Kontor. Deshalb überlebt ein angefangener Vertrag das Neuladen der Seite und
 * taucht unter „Verträge" auf. Die Entwurfs-ID steht in der Adresse, damit auch
 * ein Lesezeichen oder ein zweiter Anlauf denselben Entwurf trifft.
 *
 * Die Schritte stehen in `components/wizard/*`, das Gartenprofil mit eigenem
 * Autosave in `hooks/useGartenprofil.ts`, das Layout in `styles/wizard.css`.
 * Hier bleibt der Vertragsstand: Entwurf laden, anlegen, fortschreiben.
 */
export default function WizardPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { data: katalog = [] } = useKatalog();
  const { data: kandidaten = [] } = useKandidaten();
  const { data: vertraege = [], isSuccess: vertraegeGeladen } = useVertraege();
  const create = useCreateVertrag();
  const patch = usePatchVertrag();
  const verwerfen = useVertragVerwerfen();

  const [step, setStep] = useState(0);
  const [kundeId, setKundeId] = useState(params.get("kunde") || "");
  const [entwurfId, setEntwurfId] = useState(params.get("entwurf") || "");
  // Wer einen Entwurf weiterbearbeitet, arbeitet an einem Vertrag, der bereits
  // einem Kunden gehört. Ihn hier zu wechseln würde Gartenprofil, Mengen und
  // Preise stillschweigend auf den falschen Garten umhängen — deshalb einmal
  // beim Öffnen festgehalten und nicht mehr freigegeben.
  const [kundeGesperrt] = useState(() => !!params.get("entwurf"));
  const [suche, setSuche] = useState("");
  // Die Suche geht an den Server — verzögert, damit nicht jeder Tastendruck
  // eine Anfrage auslöst.
  const [sucheServer, setSucheServer] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setSucheServer(suche), 250);
    return () => clearTimeout(t);
  }, [suche]);
  const { data: kunden = [] } = useKundenListe(sucheServer);
  const [positionen, setPositionen] = useState<VertragPosition[]>([]);
  const [legacyGeladen, setLegacyGeladen] = useState(false);
  const [faktor, setFaktor] = useState(1.0);
  const [beginn, setBeginn] = useState("");
  // Die Erstlaufzeit wird nicht mehr eingegeben: das Vertragsjahr ist das
  // Kalenderjahr (§ 3 Abs. 2), der Vertrag endet am 31.12. des Beginnjahres.
  // Aus dem Monat folgt alles Übrige — Beginn im September heißt vier Monate.
  // Bewusst aus dem String gerechnet und nicht über `new Date`: dort verschiebt
  // die Zeitzone ein Datum um Mitternacht auf den Vormonat.
  const beginnMonat = beginn ? Number(beginn.slice(5, 7)) : 0;
  const beginnJahr = beginn ? beginn.slice(0, 4) : "";
  const laufzeit = beginnMonat ? 13 - beginnMonat : 12;
  // Vorgabe 1 Monat, nicht 3: gegenüber Verbrauchern ist alles darüber nach
  // § 309 Nr. 9 lit. c BGB unwirksam. Für Firmen folgt 3 aus der Vertragsart.
  const [kuend, setKuend] = useState(1);
  const [vertragsart, setVertragsart] = useState("verbraucher");
  const [zahlweise, setZahlweise] = useState("monatlich");
  // Die Zahlungsart ändert den Vertragstext: bei Lastschrift wird nichts
  // überwiesen, sondern eingezogen — mit Vorabankündigung, Rücklastschrift-
  // regel und Mandat als Anlage. Vorgabe seit 22.09.2026 Lastschrift
  // (Benjamin: für den Kunden am einfachsten, das Mandat läuft über Qonto);
  // der Kunde kann im Zahlungsschritt trotzdem auf Überweisung wechseln.
  const [zahlungsart, setZahlungsart] = useState("lastschrift");
  // Was nur für diesen Garten gilt — steht beim Kunden unter „Für Ihren Garten
  // vereinbart" und im Vertrag.
  const [sonder, setSonder] = useState("");
  const [fehler, setFehler] = useState("");

  // ── Entwurf laden ────────────────────────────────────────────────────────
  // `geladen` ist die Ampel für alles Weitere: solange ein Entwurf aus der
  // Adresse noch unterwegs ist, darf weder ein neuer angelegt noch etwas
  // gespeichert werden — sonst überschreibt der leere Anfangszustand den Stand.
  const [geladen, setGeladen] = useState(!params.get("entwurf"));
  const { data: entwurf } = useVertrag(entwurfId || undefined);

  useEffect(() => {
    if (geladen || !entwurf) return;
    setKundeId(entwurf.kunde_id);
    setPositionen((entwurf.leistungen || []).map(positionAusGespeichert));
    setFaktor(Number(entwurf.faktor) || 1);
    setBeginn(entwurf.beginn || "");
    setKuend(entwurf.kuendigungsfrist_monate || 1);
    setVertragsart(entwurf.vertragsart || "verbraucher");
    setZahlweise(entwurf.zahlweise || "monatlich");
    setZahlungsart(entwurf.zahlungsart || "lastschrift");
    setSonder(entwurf.sondervereinbarungen || "");
    setLegacyGeladen(true); // kein Legacy-Vorschlag über einen echten Stand kippen
    setGeladen(true);
  }, [entwurf, geladen]);

  // Anfahrt und Entsorgung folgen ihren Leistungen (Mig 187). Der Server
  // rechnet beim Speichern dasselbe; hier, damit man es sofort sieht.
  // `nebenkostenNachziehen` gibt ohne Änderung dieselbe Liste zurück — der
  // Effekt stößt sich also nicht selbst an.
  useEffect(() => {
    if (katalog.length === 0) return;
    const neu = nebenkostenNachziehen(positionen, katalog);
    if (neu !== positionen) setPositionen(neu);
  }, [positionen, katalog]);

  // Die Kündigungsfrist folgt aus der Vertragsart — Verbraucher 1 Monat
  // (§ 309 Nr. 9 BGB), Firmen 3. Sonst stünde nach einem Wechsel eine Frist im
  // Vertrag, die der Server beim Festschreiben stillschweigend zurückzöge.
  useEffect(() => {
    const soll = vertragsart === "verbraucher" ? 1 : 3;
    if (kuend !== soll) setKuend(soll);
  }, [vertragsart, kuend]);

  function entwurfInAdresse(id: string) {
    const p = new URLSearchParams(params);
    p.set("entwurf", id);
    setParams(p, { replace: true });
  }

  // `verlaengerung_monate = 0` heißt „auf unbestimmte Zeit". Für Verbraucher ist
  // das die einzige zulässige Form der stillschweigenden Verlängerung
  // (§ 309 Nr. 9 lit. b BGB); der Server erzwingt es zusätzlich. Bei Firmen
  // sind es 12 und nicht `laufzeit`: verlängert wird um ein volles Kalenderjahr.
  const verlaengerung = vertragsart === "verbraucher" ? 0 : 12;

  // ── Entwurf anlegen bzw. fortsetzen ──────────────────────────────────────
  // Der jeweils aktuelle Stand als Ref — gebraucht in der Rückmeldung des
  // Anlegens, wo die State-Werte des damaligen Renders schon veraltet wären.
  const standRef = useRef<Partial<VertragCreateBody>>({});
  const anlegenLaeuft = useRef(false);
  useEffect(() => {
    if (!kundeId || entwurfId || !geladen || !vertraegeGeladen) return;
    if (anlegenLaeuft.current) return;
    anlegenLaeuft.current = true;
    // Hat dieser Kunde schon einen offenen Entwurf, wird der fortgesetzt statt
    // ein zweiter angelegt — sonst sammeln sich Halbfertige beim selben Kunden.
    const offen = vertraege.find(v => v.kunde_id === kundeId && v.status === "entwurf");
    if (offen) {
      setEntwurfId(offen.id);
      setGeladen(false);
      entwurfInAdresse(offen.id);
      return;
    }
    create.mutateAsync({
      kunde_id: kundeId, vertragsart, beginn: beginn || null, laufzeit_monate: laufzeit,
      verlaengerung_monate: verlaengerung,
      kuendigungsfrist_monate: kuend,
      zahlweise, zahlungsart, faktor, sondervereinbarungen: "",
      leistungen: positionen,
    }).then(v => {
      setEntwurfId(v.id);
      entwurfInAdresse(v.id);
      // Was während des Anlegens schon eingegeben wurde, einmal nachziehen.
      void patch.mutateAsync({ id: v.id, body: standRef.current }).catch(() => {});
    }).catch(e => {
      anlegenLaeuft.current = false;
      setFehler(fehlertext(e) || "Entwurf konnte nicht angelegt werden.");
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kundeId, entwurfId, geladen, vertraegeGeladen, vertraege]);

  // Der gewählte Kunde kommt notfalls einzeln: über `?kunde=` geöffnet steht
  // er nicht zwingend in der Liste — und ohne ihn fehlten Name, Anschrift und
  // die Wahl Verbraucher/Unternehmer.
  const { data: einzeln } = useKunde(kundeId || undefined);
  const chosen = useMemo(
    () => kunden.find(k => k.id === kundeId) ?? (einzeln && einzeln.id === kundeId ? einzeln : undefined),
    [kunden, kundeId, einzeln]);
  const g = useGartenprofil(kundeId, chosen);

  // ── Vertragsstand ────────────────────────────────────────────────────────
  const vertragStand = useMemo(() => ({
    vertragsart, beginn: beginn || null, laufzeit_monate: laufzeit,
    verlaengerung_monate: verlaengerung,
    kuendigungsfrist_monate: kuend, zahlweise, zahlungsart, faktor,
    sondervereinbarungen: sonder,
    leistungen: positionen.map((p, i) => ({ ...p, sort_order: i })),
  }), [vertragsart, beginn, laufzeit, verlaengerung, kuend, zahlweise, zahlungsart,
       faktor, sonder, positionen]);
  standRef.current = vertragStand;

  const vertragSave = useAutoSave(
    vertragStand,
    w => patch.mutateAsync({ id: entwurfId, body: w }),
    { aktiv: !!entwurfId && geladen },
  );
  const speicherStand = vertragSave.stand === "ruht" ? g.speichern.stand : vertragSave.stand;

  // Legacy-Pflegemonate als Positionsvorschläge (einmalig, nur bei leerem Entwurf).
  useEffect(() => {
    if (legacyGeladen || !kundeId || katalog.length === 0) return;
    const k = kandidaten.find(x => x.id === kundeId);
    if (k?.pflege_monate_json) {
      const v = positionenAusLegacy(k.pflege_monate_json, katalog);
      if (v.length) setPositionen(v);
    }
    setLegacyGeladen(true);
  }, [kundeId, katalog, kandidaten, legacyGeladen]);

  // Prüfliste nur im letzten Schritt holen — dort wird sie gebraucht, und dort
  // ist der Stand gespeichert.
  const { data: pruefung } = useVertragPruefung(entwurfId || undefined, step === 4);
  const zustimmung = useZustimmungErstellen(entwurfId || undefined);
  const [mailOffen, setMailOffen] = useState(false);
  const kalk = useMemo(() => kalkVorschau(positionen, faktor), [positionen, faktor]);

  // Schritt „Leistungen" zum ersten Mal, Vertrag noch leer, Garten erfasst:
  // die Grundausstattung steht gleich drin, mit den Größen aus dem Garten,
  // dazu die Anfahrt, die sich aus den Einsätzen rechnet. Einmal je Sitzung —
  // wer danach alles löscht, will es leer haben.
  const vorbefuellt = useRef(false);
  useEffect(() => {
    if (step !== 2 || vorbefuellt.current || !geladen || katalog.length === 0) return;
    vorbefuellt.current = true;
    if (positionen.length > 0) return;
    const grund = grundausstattung(katalog, g.garten, faktor);
    if (!grund.length) return;
    const anfahrt = katalog.find(k => k.einheit === "anfahrt");
    const neu = grund.map(v => positionAusKatalog(v.katalog, v.menge));
    if (anfahrt) neu.push(positionAusKatalog(anfahrt, 1, []));
    setPositionen(neu.map((p, i) => ({ ...p, sort_order: i })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, geladen, katalog]);

  /** Kundenwechsel: ein noch leerer Entwurf wird weggeräumt statt liegen
   *  gelassen — sonst steht unter „Verträge" ein Entwurf ohne Inhalt. */
  async function kundeWechseln() {
    if (entwurfId && positionen.length === 0) {
      try { await verwerfen.mutateAsync(entwurfId); } catch { /* bleibt sonst als Entwurf stehen */ }
    }
    setEntwurfId(""); setKundeId(""); setPositionen([]);
    g.zuruecksetzen();
    setLegacyGeladen(false); setGeladen(true);
    anlegenLaeuft.current = false;
    setParams(new URLSearchParams(), { replace: true });
    setStep(0);
  }

  /** Beim Verlassen wird noch Ausstehendes weggeschrieben — sonst verlöre der
   *  letzte Tastendruck sein Zeitfenster (1,2 s) im Moment des Wegklickens. */
  async function allesSpeichern() {
    await vertragSave.jetztSpeichern();
    await g.speichern.jetztSpeichern();
  }
  async function schliessen() {
    try { await allesSpeichern(); } catch { /* Entwurf bleibt wie zuletzt gespeichert */ }
    navigate("/vertraege");
  }
  async function fertig() {
    setFehler("");
    try { await allesSpeichern(); navigate(`/vertraege/${entwurfId}`); }
    catch (e) { setFehler(fehlertext(e) || "Speichern fehlgeschlagen."); }
  }
  /** „Angebot senden": speichern, festschreiben (Nummer, Link, PIN), dann die
   *  Mail zeigen. Gesendet wird erst nach dem zweiten Klick in der Vorschau. */
  async function angebotSenden() {
    setFehler("");
    try { await allesSpeichern(); await zustimmung.mutateAsync(); setMailOffen(true); }
    catch (e) { setFehler(fehlertext(e) || "Das Angebot ließ sich nicht vorbereiten."); }
  }
  async function entwurfVerwerfen() {
    if (!entwurfId) return;
    try { await verwerfen.mutateAsync(entwurfId); navigate("/vertraege"); }
    catch (e) { setFehler(fehlertext(e) || "Verwerfen fehlgeschlagen."); }
  }

  const canNext = step !== 0 || !!kundeId;
  const ohneMonate = positionen.filter(p => p.monate.length === 0).length;

  return (
    <Shell
      title="Neuer Pflegevertrag"
      subtitle={chosen ? chosen.name : "Vertrag in wenigen Schritten anlegen"}
      action={
        <div className="pf-wz-aktion">
          <span className={"pf-wz-speicher" + (speicherStand === "fehler" ? " is-fehler" : "")}>
            {SPEICHER_TEXT[speicherStand]}
          </span>
          <button className="pf-btn is-ghost" onClick={schliessen}>Schließen</button>
        </div>
      }
    >
      <div className="pf-steps">
        {STEPS.map((label, i) => (
          <button key={label}
            className={"pf-step" + (step === i ? " is-on" : i < step ? " is-done" : "")}
            onClick={() => i < step && setStep(i)}
          >{i + 1}. {label}</button>
        ))}
      </div>

      {/* Schritt „Leistungen" bekommt die volle Breite — dort steht der
          Jahresplan als zweite Spalte. Alle anderen Schritte bleiben eine
          lesbare Formularspalte. */}
      <div className={step === 2 ? undefined : "pf-narrow"}>
        {step === 0 && (
          <SchrittKunde chosen={chosen} gesperrt={kundeGesperrt} onWechseln={kundeWechseln}
            suche={suche} setSuche={setSuche} sucheFertig={sucheServer === suche}
            treffer={kunden.slice(0, 40)} onWaehlen={setKundeId}
            vertragsart={vertragsart} setVertragsart={setVertragsart} fehler={fehler} />
        )}
        {step === 1 && <SchrittGarten g={g} />}
        {step === 2 && (
          <SchrittLeistungen positionen={positionen} setPositionen={setPositionen} katalog={katalog}
            faktor={faktor} garten={g.garten} gartenErfasst={g.erfasst} kalk={kalk}
            ohneMonate={ohneMonate} onZurueckZumGarten={() => setStep(1)} />
        )}
        {step === 3 && <SchrittPreis faktor={faktor} setFaktor={setFaktor} kalk={kalk} ohneMonate={ohneMonate} />}
        {step === 4 && (
          <SchrittSenden
            v={{ name: chosen?.name, beginn, setBeginn, sonder, setSonder, laufzeit, beginnJahr, kuend,
                 istVerbraucher: vertragsart === "verbraucher", faktor, positionen, kalk, ohneMonate, pruefung, fehler }}
            entwurfId={entwurfId} mailOffen={mailOffen} onMailZu={() => setMailOffen(false)}
            onGesendet={() => navigate(`/vertraege/${entwurfId}`)}
            onVerwerfen={entwurfVerwerfen} verwerfenLaeuft={verwerfen.isPending} />
        )}
      </div>

      <div className="pf-actions pf-narrow">
        {step > 0 && <button className="pf-btn is-ghost" onClick={() => setStep(step - 1)}>Zurück</button>}
        {step < 4 ? (
          <button className="pf-btn pf-spacer" disabled={!canNext}
            onClick={() => { if (step === 1) void g.speichern.jetztSpeichern(); setStep(step + 1); }}>Weiter</button>
        ) : (
          <>
            <button className="pf-btn is-ghost pf-spacer" disabled={!entwurfId || positionen.length === 0} onClick={fertig}>
              Später senden
            </button>
            {!mailOffen && (
              <button className="pf-btn"
                disabled={!entwurfId || positionen.length === 0 || zustimmung.isPending || (pruefung ? !pruefung.ok : false)}
                onClick={angebotSenden}>
                {zustimmung.isPending ? "Bereite vor…" : "Angebot senden …"}
              </button>
            )}
          </>
        )}
      </div>
    </Shell>
  );
}
